"use client"

import { useCallback, useEffect, useRef } from "react"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useEntityType } from "@/contexts/entity-type-context"
import { onTabRefocus } from "@/lib/on-tab-refocus"

/**
 * Stage count fetching — purely imperative.
 *
 * Returns `refreshStageCounts` which callers invoke explicitly after
 * actions that change stage membership (move, extend, bulk action, etc.).
 *
 * Stage counts are fetched:
 *   1. Once on initialization (via `useStageCountsInit` in workflow-hub)
 *   2. After explicit user actions (move-to, extend, bulk actions)
 *   3. On a 5-minute polling interval (set up in workflow-hub)
 *   4. When global filters change (debounced, single source in workflow-hub)
 */
export function useStageFacets() {
  const { globalFilters, configuration, setStageCounts } = useWorkflow()
  const { searchWorkflowItems } = useWorkflowApi()
  const { entityType } = useEntityType()
  // Shared gw-client-request-index entities (VmsRequest / VmsEmail / ClientRequest /
  // AtsPlacementRequest) have no Stage column to facet on, so the stage-count probe below
  // must be skipped for all of them. ClientRequest + AtsPlacementRequest were missing here,
  // so their tabs fired the `Stage ne null …` facet query and it errored. Keep in lock-step
  // with workflow-grid's isRequestEntityType.
  const isRequestEntityType = entityType === 'VmsRequest' || entityType === 'VmsEmail'
    || entityType === 'ClientRequest' || entityType === 'AtsPlacementRequest'

  // Deduplication: track the latest request to ignore stale responses
  const requestIdRef = useRef(0)

  const refreshStageCounts = useCallback(async () => {
    if (!configuration) return

    // VMS sub-tabs aren't carved into stages — they have a single "All" stage
    // with no Stage column to facet on. Skip the count call entirely; the
    // selector pill counts come from useVmsConfig instead.
    if (isRequestEntityType) return

    const currentRequestId = ++requestIdRef.current

    try {
      const globalFilterStrings = globalFilters.map(f => f.filter).filter(Boolean)

      const filterParts = [
        "DocumentType eq 'Assignment'",
        "Stage ne null",
        "Stage ne ''",
        "Stage ne 'Tasks'",
        ...globalFilterStrings,
      ]
      const filter = filterParts.join(" and ")

      const result = await searchWorkflowItems(
        undefined,
        undefined,
        [],
        1,
        0,
        "*",
        "id",
        filter,
        undefined,
        ["Stage"]
      )

      // Ignore stale responses (a newer request was made while this one was in-flight)
      if (currentRequestId !== requestIdRef.current) return

      const stageFacets = result.facets?.["Stage"]
      if (!stageFacets) return

      const counts: Record<string, number> = {}
      for (const { value, count } of stageFacets) {
        counts[value.toLowerCase()] = count
      }
      setStageCounts(counts)
    } catch {
      // Non-critical — tab counts are best-effort
    }
  }, [searchWorkflowItems, globalFilters, configuration, setStageCounts, isRequestEntityType])

  return { refreshStageCounts }
}

/**
 * Call this hook ONCE (in workflow-hub) to set up:
 *   - Initial stage count fetch when configuration is ready
 *   - 5-minute polling interval
 *   - Re-fetch when global filters change (debounced, single instance)
 *
 * `paused` suspends the poll + filter-change refreshes while the WO stage
 * strip isn't the active surface (user is on a Requests tab — ATS/VMS/CR,
 * where the WO stage badges aren't even visible). On resume the counts
 * refresh once immediately so the strip is fresh when the user returns.
 */
export function useStageCountsInit(paused = false) {
  const { globalFilters, configuration } = useWorkflow()
  const { refreshStageCounts } = useStageFacets()
  const hasInitRef = useRef(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const filterChangeDebounceRef = useRef<NodeJS.Timeout | null>(null)
  const prevFilterKeyRef = useRef<string | null>(null)

  // Keep a ref to the latest refreshStageCounts so polling always uses current filters
  const refreshStageCountsRef = useRef(refreshStageCounts)
  useEffect(() => {
    refreshStageCountsRef.current = refreshStageCounts
  }, [refreshStageCounts])

  // Ref so the long-lived interval callback sees the current paused state.
  const pausedRef = useRef(paused)
  useEffect(() => {
    const wasPaused = pausedRef.current
    pausedRef.current = paused
    // Resume catch-up: refresh once when coming back to the WO surface, so
    // the badges reflect anything that changed while polling was suspended.
    if (wasPaused && !paused && hasInitRef.current) {
      refreshStageCountsRef.current()
    }
  }, [paused])

  // One-time init fetch, when configuration first becomes ready.
  //
  // Deliberately NO interval here. Setting the interval up in a [configuration] effect meant its cleanup
  // cleared the interval on every configuration identity change while the hasInitRef guard stopped it
  // being recreated — so the first re-dispatch of SET_CONFIGURATION (refetch, per-client custom fields,
  // …) silenced the 5-minute poll for the rest of the session. The interval now lives in its own
  // mount-once effect below.
  useEffect(() => {
    if (!configuration || hasInitRef.current) return
    hasInitRef.current = true

    // Record the current filter key so the filter-change effect skips its first run
    prevFilterKeyRef.current = globalFilters.map(f => f.filter).filter(Boolean).join('|')

    // Initial fetch — only when the WO surface is actually active. Mounting
    // directly on a Requests tab (persisted activeTab) skips it; the resume
    // effect fires the catch-up when the user returns to the WO strip.
    if (!pausedRef.current) {
      refreshStageCountsRef.current()
    }
  }, [configuration]) // eslint-disable-line react-hooks/exhaustive-deps

  // 5-minute poll + refresh on tab foreground. Set up ONCE for the component's life so a configuration
  // re-dispatch can't tear it down. The callback fires the latest refreshStageCounts via the ref (which
  // no-ops until init has run and reads the current filters).
  useEffect(() => {
    const poll = () => {
      if (!pausedRef.current && hasInitRef.current) refreshStageCountsRef.current()
    }
    intervalRef.current = setInterval(poll, 5 * 60 * 1000)

    // Fresh-on-focus: the counts matter when the user is actually looking, so refresh when the tab
    // returns to the foreground rather than waiting up to five minutes — but only for a real tab
    // switch, not the sub-second iframe reattach flicker that fires on every mouse move (see
    // onTabRefocus).
    const stopRefocus = onTabRefocus(poll)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      stopRefocus()
    }
  }, [])

  // Re-fetch when global filters change (debounced, single source of truth)
  useEffect(() => {
    if (!configuration || !hasInitRef.current) return

    const filterKey = globalFilters.map(f => f.filter).filter(Boolean).join('|')
    // Skip if filters haven't actually changed
    if (filterKey === prevFilterKeyRef.current) return
    prevFilterKeyRef.current = filterKey

    if (filterChangeDebounceRef.current) clearTimeout(filterChangeDebounceRef.current)
    filterChangeDebounceRef.current = setTimeout(() => {
      if (!pausedRef.current) refreshStageCountsRef.current()
    }, 300)

    return () => {
      if (filterChangeDebounceRef.current) clearTimeout(filterChangeDebounceRef.current)
    }
  }, [globalFilters, configuration]) // eslint-disable-line react-hooks/exhaustive-deps

  return { refreshStageCounts }
}
